export type LocationSuggestion = {
  city: string;
  state: string;
  zip?: string;
  label: string;
};

export type AddressSuggestion = {
  address: string;
  city?: string;
  state?: string;
  zip?: string;
  label: string;
};

const US_STATES: Record<string, string> = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA",
  colorado: "CO", connecticut: "CT", delaware: "DE", florida: "FL", georgia: "GA",
  hawaii: "HI", idaho: "ID", illinois: "IL", indiana: "IN", iowa: "IA",
  kansas: "KS", kentucky: "KY", louisiana: "LA", maine: "ME", maryland: "MD",
  massachusetts: "MA", michigan: "MI", minnesota: "MN", mississippi: "MS", missouri: "MO",
  montana: "MT", nebraska: "NE", nevada: "NV", "new hampshire": "NH", "new jersey": "NJ",
  "new mexico": "NM", "new york": "NY", "north carolina": "NC", "north dakota": "ND", ohio: "OH",
  oklahoma: "OK", oregon: "OR", pennsylvania: "PA", "rhode island": "RI", "south carolina": "SC",
  "south dakota": "SD", tennessee: "TN", texas: "TX", utah: "UT", vermont: "VT",
  virginia: "VA", washington: "WA", "west virginia": "WV", wisconsin: "WI", wyoming: "WY",
  "district of columbia": "DC",
};

export function stateAbbr(value?: string) {
  if (!value) return "";
  const trimmed = value.trim();
  return US_STATES[trimmed.toLowerCase()] ?? trimmed.toUpperCase();
}

export function cityFromLocation(value?: string) {
  return (value ?? "").split(",")[0].trim();
}

function uniqueByLabel<T extends { label: string }>(items: T[]) {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = item.label.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export async function resolveZip(zip: string): Promise<LocationSuggestion[]> {
  const digits = zip.replace(/\D/g, "").slice(0, 5);
  if (digits.length !== 5) return [];
  const response = await fetch(`https://api.zippopotam.us/us/${digits}`);
  if (!response.ok) return [];
  const data = await response.json();
  return uniqueByLabel((data.places ?? []).map((place: any) => {
    const city = String(place["place name"] ?? "");
    const state = stateAbbr(place["state abbreviation"] ?? place.state);
    return { city, state, zip: digits, label: [city, state].filter(Boolean).join(", ") };
  }).filter((item: LocationSuggestion) => item.city));
}

export async function fetchLocationSuggestions(query: string): Promise<LocationSuggestion[]> {
  const trimmed = query.trim();
  if (trimmed.length < 2) return [];
  const digits = trimmed.replace(/\D/g, "");
  if (/^\d{5}$/.test(digits)) return resolveZip(digits);

  const response = await fetch(
    `https://photon.komoot.io/api/?q=${encodeURIComponent(trimmed)}&limit=8&osm_tag=place:city&osm_tag=place:town&osm_tag=place:village&lang=en`,
  );
  if (!response.ok) return [];
  const data = await response.json();
  const items = (data.features ?? []).map((feature: any) => {
    const props = feature.properties ?? {};
    if (props.countrycode && props.countrycode !== "US") return null;
    const city = String(props.name ?? "").trim();
    const state = stateAbbr(props.state);
    if (!city) return null;
    return { city, state, label: [city, state].filter(Boolean).join(", ") };
  }).filter(Boolean) as LocationSuggestion[];
  return uniqueByLabel(items);
}

export async function fetchAddressSuggestions(query: string): Promise<AddressSuggestion[]> {
  const trimmed = query.trim();
  if (trimmed.length < 3) return [];
  const response = await fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(trimmed)}&limit=8&lang=en`);
  if (!response.ok) return [];
  const data = await response.json();
  const items = (data.features ?? []).map((feature: any) => {
    const props = feature.properties ?? {};
    if (props.countrycode && props.countrycode !== "US") return null;
    const street = [props.housenumber, props.street].filter(Boolean).join(" ");
    const address = String(street || props.name || "").trim();
    if (!address) return null;
    const city = props.city || props.town || props.village || props.county;
    const state = stateAbbr(props.state);
    const zip = props.postcode ? String(props.postcode).slice(0, 5) : undefined;
    return { address, city, state, zip, label: [address, city, state, zip].filter(Boolean).join(", ") };
  }).filter(Boolean) as AddressSuggestion[];
  return uniqueByLabel(items);
}